/**
 * TeamSystem Impact - Sustainability KPI Data
 * Costanti tipizzate estratte dal SustainabilityReportPDF
 */

export type Status = 'PASS' | 'PARTIAL' | 'NEEDS WORK' | 'EXCELLENT' | 'N/A';

export interface SIARow {
  area: string;
  status: Status;
  notes: string;
}

export interface GSFPrinciple {
  principle: string;
  score: string;
  numericScore: number;
  maxScore: number;
  status: Status;
  keyFinding: string;
}

export interface SCIMetric {
  label: string;
  value: string;
  unit: string;
}

export interface PriorityAction {
  n: string;
  action: string;
  priority: 'High' | 'Medium' | 'Low';
  impact: string;
}

// ─── SIA ────────────────────────────────────────────────
export const SIA_ROWS: SIARow[] = [
  { area: 'A1. Carbon & Energy', status: 'PASS', notes: 'React.lazy route splitting, manualChunks (recharts/react-pdf/mui), recharts sparkline replaced with CSS' },
  { area: 'A2. Hardware & E-Waste', status: 'PASS', notes: 'No heavy assets, no GPU requirements, works on older devices' },
  { area: 'A3. Data & Storage', status: 'PASS', notes: 'Blob URLs revoked, no persistent storage, on-demand PDF' },
  { area: 'A4. Water & Resources', status: 'PASS', notes: 'Cloud WUE tracked (~18 mL/year), GHG Protocol Scope 2 dual reporting' },
  { area: 'A5. Biodiversity', status: 'PASS', notes: 'LCIA quantified, TNFD LEAP 4/4 complete, supply chain assessed, ADRs documented' },
  { area: 'B1. Accessibility & Inclusion', status: 'PASS', notes: 'ARIA labels on charts, KPI cards, tables, regions; WCAG AA compliant' },
  { area: 'B2. Privacy & User Well-Being', status: 'PASS', notes: 'No tracking, no dark patterns, data stays client-side' },
  { area: 'B3. Supply Chain & Labor', status: 'PASS', notes: 'Dependencies are mainstream OSS (MUI, recharts, react-pdf), supply chain material assessment completed' },
  { area: 'C1. Value Distribution', status: 'PASS', notes: 'Client-side PDF (zero server cost), OSS tools' },
  { area: 'C2. Sustainable Business Model', status: 'PASS', notes: 'No vendor lock-in, MIT-licensed dependencies' },
];

export const SIA_PILLARS = [
  { label: 'Environmental', labelIT: 'Ambientale', status: 'PASS' as Status, detail: 'Code splitting, bundle optimization, TNFD LEAP complete', detailIT: 'Code splitting, ottimizzazione bundle, TNFD LEAP completo' },
  { label: 'Social', labelIT: 'Sociale', status: 'PASS' as Status, detail: 'WCAG AA compliant, full i18n IT/EN, ARIA labels', detailIT: 'WCAG AA conforme, i18n completo IT/EN, ARIA labels' },
  { label: 'Economic', labelIT: 'Economico', status: 'PASS' as Status, detail: 'OSS, no vendor lock-in, client-side PDF', detailIT: 'OSS, no lock-in, PDF client-side' },
] as const;

// ─── GSF Principles ─────────────────────────────────────
export const GSF_PRINCIPLES: GSFPrinciple[] = [
  { principle: '1. Carbon Efficiency', score: '4/5', numericScore: 4, maxScore: 5, status: 'PASS', keyFinding: 'manualChunks isolate recharts/react-pdf/mui, sparkline replaced with CSS' },
  { principle: '2. Energy Efficiency', score: '3.5/5', numericScore: 3.5, maxScore: 5, status: 'PARTIAL', keyFinding: 'React.lazy on 3 routes, stakeholder summary endpoint, Web Vitals monitoring' },
  { principle: '3. Carbon Awareness', score: 'N/A', numericScore: 0, maxScore: 5, status: 'N/A', keyFinding: 'Not applicable to frontend module (no batch/scheduled work)' },
  { principle: '4. Hardware Efficiency', score: '4/5', numericScore: 4, maxScore: 5, status: 'PASS', keyFinding: 'SVG-only, no heavy assets, progressive enhancement' },
  { principle: '5. Measurement', score: '3/5', numericScore: 3, maxScore: 5, status: 'PARTIAL', keyFinding: 'SCI tracking script, bundle visualizer, Web Vitals, CI budget check' },
  { principle: '6. Climate Commitments', score: '4/5', numericScore: 4, maxScore: 5, status: 'PASS', keyFinding: 'ADRs documented, TNFD LEAP complete, supply chain assessed' },
];

export const OVERALL_GSF_SCORE = 3.7;
export const GSF_MATURITY_LEVEL = 3;
export const GSF_MATURITY_LABEL = 'Practiced';
export const GSF_TARGET_LEVEL = 4;
export const GSF_TARGET_LABEL = 'Optimizing';

// ─── Carbon-Aware Architecture ──────────────────────────
export const CARBON_INTENSITY = 310; // gCO2eq/kWh Italy North
export const CARBON_REGION = 'Italy North (Milan)';
export const CARBON_PUE = 1.1;

export const CARBON_STRATEGIES = [
  { strategy: 'Deploy backend in low-carbon region (Finland/France)', impact: '-84% operational carbon' },
  { strategy: 'Azure datacenter PUE 1.1', impact: 'Best-in-class efficiency' },
  { strategy: 'Client-side PDF (zero server compute)', impact: 'Greenest document generation' },
];

// ─── SCI Measurement ────────────────────────────────────
export const SCI_PAGE_VIEW = 0.0086;   // gCO2eq/page view
export const SCI_PDF = 0.019;          // gCO2eq/PDF generation
export const SCI_ANNUAL = 45;          // gCO2eq/year (20 users)

export const PAGE_VIEW_ENERGY: SCIMetric[] = [
  { label: 'Backend (6 API calls, ~600ms)', value: '0.00000236', unit: 'kWh' },
  { label: 'Frontend (page render + chart, ~2s)', value: '0.00000833', unit: 'kWh' },
  { label: 'Network (~50KB API responses)', value: '0.00000035', unit: 'kWh' },
  { label: 'MongoDB (6 queries, ~180ms)', value: '0.00000069', unit: 'kWh' },
  { label: 'E total', value: '0.00001173', unit: 'kWh' },
];

export const PDF_ENERGY: SCIMetric[] = [
  { label: 'Client CPU (PDF render, ~4s)', value: '0.0000278', unit: 'kWh' },
  { label: 'Network (chunk download ~400KB)', value: '0.0000028', unit: 'kWh' },
  { label: 'E total', value: '0.0000306', unit: 'kWh' },
];

export const MONTHLY_BREAKDOWN = [
  { activity: 'Page views', sci: '0.0086 gCO2eq', volume: '~260', total: '2.24 gCO2eq' },
  { activity: 'PDF generation', sci: '0.019 gCO2eq', volume: '~80', total: '1.52 gCO2eq' },
  { activity: 'Total', sci: '', volume: '', total: '3.76 gCO2eq/month' },
];

export const KEY_INSIGHTS = [
  'Embodied carbon (M) dominates — 50-57% of SCI. Client device manufacturing emissions exceed operational energy.',
  'PDF generation is 100% client-side — zero server cost. The greenest possible architecture for document generation.',
  'Italy grid is carbon-intensive (310 gCO2eq/kWh). Deploying backend in Finland (~50) or France (~60) would cut operational carbon by 84%.',
  '45 gCO2eq/year is equivalent to driving a car ~0.15 km or charging a smartphone ~5 times.',
  'The module is a sustainability enabler: it produces CSRD/ESRS double materiality reports helping organizations assess ESG impact.',
];

// ─── Cloud Emission Measurement (GHG Protocol / ISO 14064) ──
export interface ITEUComponent {
  category: string;
  categoryIT: string;
  energyKwh: number;
  percentage: number;
  detail: string;
}

export interface ScopeRow {
  scope: string;
  description: string;
  descriptionIT: string;
  value: string;
  notes: string;
  notesIT: string;
}

export interface EmissionMethod {
  method: string;
  value: string;
  unit: string;
  source: string;
}

// Infrastructure assumptions (aligned with SCI declared boundary)
export const CLOUD_PROVIDER = 'Azure';
export const CLOUD_INSTANCE = 'App Service B1 (1 vCPU, 1.75 GiB RAM)';
export const CLOUD_DATABASE = 'MongoDB Atlas M10';
export const CLOUD_FRONTEND = 'Static files via CDN';
export const SERVER_LIFETIME_YEARS = 6; // Azure published 2024

// ITEU Decomposition — annual server-side energy (20 users, ~3,120 page views/year)
// Backend E per page view = 0.00000236 kWh (from SCI), MongoDB = 0.00000069, Network = 0.00000035
export const ITEU_ANNUAL_KWH = 0.0106; // Total ITEU kWh/year (server-side only)
export const TFE_ANNUAL_KWH = 0.0117;  // ITEU × PUE 1.1

export const ITEU_COMPONENTS: ITEUComponent[] = [
  { category: 'Compute', categoryIT: 'Calcolo', energyKwh: 0.00736, percentage: 69, detail: 'Fastify API — 6 calls/page, ~600ms' },
  { category: 'Storage', categoryIT: 'Archiviazione', energyKwh: 0.00215, percentage: 20, detail: 'MongoDB Atlas — 6 queries/page, ~180ms' },
  { category: 'Networking', categoryIT: 'Rete', energyKwh: 0.00109, percentage: 10, detail: 'Intra-region API responses ~50KB/page' },
  // Memory is bundled into compute estimate for standard B1 instance (non-memory-optimized)
];

// Usage Emissions = TFE × GCI
export const USAGE_EMISSIONS_LOCATION = 3.62;  // gCO2eq/year (location-based: TFE × 310)
export const USAGE_EMISSIONS_MARKET = 0;        // gCO2eq/year (market-based: Azure 100% renewable RECs)

// Embodied Emissions — server hardware lifecycle allocation
// Server embodied ~1,200 kgCO2e, 6-year lifetime, 1/64 vCPU share, 8,760h active
export const EMBODIED_SERVER_KG = 1200;        // kgCO2e total server
export const EMBODIED_USER_SHARE = 0.56;       // gCO2eq/year allocated to our 1-vCPU workload
// Breakdown: CPU 45%, RAM 25%, Motherboard 15%, PSU 10%, Chassis/NIC 5%

// Total Cloud Emissions
export const TOTAL_CLOUD_EMISSIONS_LOCATION = 4.18;  // gCO2eq/year (usage + embodied, location-based)
export const TOTAL_CLOUD_EMISSIONS_MARKET = 0.56;     // gCO2eq/year (usage + embodied, market-based)

// Water Usage Effectiveness
export const AZURE_WUE = 1.0;           // L/kWh (Azure Italy North cooling)
export const REGION_ELECTRICITY_WUE = 0.5; // L/kWh (Italy grid electricity production water)
export const WATER_COOLING_LITERS = 0.0117;  // TFE × WUE
export const WATER_ELECTRICITY_LITERS = 0.0064; // E × PUE × region WUE
export const WATER_TOTAL_LITERS = 0.018;  // ~18 mL/year

// GHG Scope Mapping
export const GHG_SCOPES: ScopeRow[] = [
  {
    scope: 'Scope 1',
    description: 'Direct emissions',
    descriptionIT: 'Emissioni dirette',
    value: '0',
    notes: 'No owned infrastructure — fully cloud-hosted',
    notesIT: 'Nessuna infrastruttura propria — interamente in cloud',
  },
  {
    scope: 'Scope 2',
    description: 'Purchased electricity',
    descriptionIT: 'Elettricita acquistata',
    value: '0',
    notes: 'Electricity purchased by cloud provider, not by us',
    notesIT: 'Elettricita acquistata dal provider cloud, non da noi',
  },
  {
    scope: 'Scope 3 (Cat. 1)',
    description: 'Purchased services',
    descriptionIT: 'Servizi acquistati',
    value: '4.18 gCO2eq',
    notes: 'Cloud hosting (Azure + MongoDB Atlas) — our responsibility',
    notesIT: 'Cloud hosting (Azure + MongoDB Atlas) — nostra responsabilita',
  },
];

// Location vs Market comparison
export const EMISSION_METHODS: EmissionMethod[] = [
  { method: 'Location-based', value: '4.18', unit: 'gCO2eq/year', source: 'EMBER 2024, Italy grid avg 310 gCO2eq/kWh' },
  { method: 'Market-based', value: '0.56', unit: 'gCO2eq/year', source: 'Azure 100% renewable matching (RECs)' },
];

// Hyperscaler PUE comparison
export const PUE_COMPARISON = [
  { provider: 'Google Cloud', pue: '1.10', notes: 'Fleet average, published per-site' },
  { provider: 'AWS', pue: '~1.15-1.20', notes: 'Estimated, less granular' },
  { provider: 'Azure (Italy North)', pue: '1.12', notes: 'Region-specific, current deployment' },
  { provider: 'Industry Average', pue: '1.58', notes: 'Uptime Institute 2024' },
];

// Embodied emissions breakdown
export const EMBODIED_BREAKDOWN = [
  { component: 'CPU', percentage: 45, detail: 'Die manufacturing, lithography, packaging' },
  { component: 'RAM (DDR4)', percentage: 25, detail: 'DRAM density, die area, PCB' },
  { component: 'Motherboard', percentage: 15, detail: 'Server-grade PCB LCA' },
  { component: 'PSU (2x)', percentage: 10, detail: 'Weight-based, redundant supply' },
  { component: 'Chassis + NIC', percentage: 5, detail: 'Rack enclosure, network interface' },
];

// ─── Digital Biodiversity (LCIA / ReCiPe 2016 / TNFD) ──

export interface BiodiversityPathway {
  pathway: string;
  pathwayIT: string;
  value: string;
  unit: string;
  method: string;
  notes: string;
}

export interface MaterialCF {
  material: string;
  cfSpeciesYrPerKg: number;
  massKg: number;
  impact: number; // species·yr
  notes: string;
}

export interface TNFDPhase {
  phase: string;
  phaseIT: string;
  action: string;
  actionIT: string;
  status: Status;
}

export interface SitingCriterion {
  criterion: string;
  criterionIT: string;
  ourValue: string;
  rating: 'Low' | 'Medium' | 'High';
  notes: string;
}

// Climate → Biodiversity: Usage emissions × ReCiPe 2016 Endpoint CF
// UE = 4.18 gCO2eq/year = 0.00000418 kgCO2e/year
// CF = 2.80e-9 species·yr per kgCO2e
export const CLIMATE_BIODIV_CF = 2.80e-9; // species·yr per kgCO2e (ReCiPe 2016)
export const CLIMATE_BIODIV_IMPACT = 1.17e-14; // 0.00000418 × 2.80e-9

// Land Use: Azure Italy North datacenter ~20,000 m², urban/industrial site
// CF urban/industrial = 0.05 PDF·m²·yr per m²·yr
// Resource share = 1/64 vCPU on ~10,000 server rack (approx 1/640,000)
export const DATACENTER_AREA_M2 = 20000;
export const LAND_USE_CF = 0.05; // PDF·m²·yr per m²·yr (urban/industrial)
export const LAND_USE_RESOURCE_SHARE = 1.56e-6; // 1 vCPU / 640,000 total capacity
export const LAND_USE_IMPACT = 1.56e-6; // 20000 × 0.05 × 1.56e-6 × 1 = 1.56e-6 PDF·m²·yr

// Material Extraction (embodied): server hardware BoM proxy
// Allocated to our 1/64 vCPU share
export const MATERIAL_CFS: MaterialCF[] = [
  { material: 'Gold', cfSpeciesYrPerKg: 1.2e-4, massKg: 0.0003, impact: 3.6e-8, notes: 'Connectors, PCB traces — dominates biodiversity impact' },
  { material: 'Copper', cfSpeciesYrPerKg: 2.5e-8, massKg: 0.15, impact: 3.75e-9, notes: 'Wiring, PCB, heatsinks' },
  { material: 'Rare Earths', cfSpeciesYrPerKg: 3.0e-6, massKg: 0.002, impact: 6.0e-9, notes: 'HDD magnets, capacitors' },
  { material: 'Cobalt', cfSpeciesYrPerKg: 4.0e-7, massKg: 0.005, impact: 2.0e-9, notes: 'Battery backup, capacitors' },
  { material: 'Silicon', cfSpeciesYrPerKg: 5.0e-9, massKg: 0.08, impact: 4.0e-10, notes: 'CPU/RAM dies' },
  { material: 'Aluminum', cfSpeciesYrPerKg: 1.5e-8, massKg: 0.5, impact: 7.5e-9, notes: 'Chassis, heatsinks' },
  { material: 'Steel', cfSpeciesYrPerKg: 3.0e-9, massKg: 2.0, impact: 6.0e-9, notes: 'Rack, chassis frame' },
];
// Total material impact (1/64 share, amortized over 6 years): ~6.2e-8 species·yr/year
export const TOTAL_MATERIAL_IMPACT = 6.2e-8; // species·yr/year allocated

// Ecotoxicity proxy (from Ecoinvent server manufacturing aggregate)
export const ECOTOXICITY_IMPACT = 1.0e-8; // species·yr/year (proxy estimate)

// Total biodiversity loss
export const TOTAL_BIODIV_IMPACT = 7.4e-8; // species·yr/year (sum of all pathways)

export const BIODIV_PATHWAYS: BiodiversityPathway[] = [
  { pathway: 'Material Extraction', pathwayIT: 'Estrazione Materiali', value: '6.2e-8', unit: 'species·yr/yr', method: 'ReCiPe 2016 + Ecoinvent', notes: 'Gold dominates (58%)' },
  { pathway: 'Ecotoxicity', pathwayIT: 'Ecotossicita', value: '1.0e-8', unit: 'species·yr/yr', method: 'Ecoinvent aggregate', notes: 'Manufacturing pollution proxy' },
  { pathway: 'Land Use', pathwayIT: 'Uso del Suolo', value: '1.56e-6', unit: 'PDF·m²·yr', method: 'ReCiPe 2016 Endpoint', notes: 'Urban/industrial site (low impact)' },
  { pathway: 'Climate Change', pathwayIT: 'Cambiamento Climatico', value: '1.17e-14', unit: 'species·yr/yr', method: 'ReCiPe 2016 Endpoint', notes: 'Negligible — tiny energy footprint' },
];

export const TNFD_PHASES: TNFDPhase[] = [
  { phase: 'Locate', phaseIT: 'Localizzare', action: 'Datacenter: Azure Italy North (Milan). Supply chain: global hardware vendors.', actionIT: 'Datacenter: Azure Italy North (Milano). Supply chain: fornitori hardware globali.', status: 'PASS' },
  { phase: 'Evaluate', phaseIT: 'Valutare', action: 'LCIA quantification completed (ReCiPe 2016). Impact pathways: material, land, climate, ecotox.', actionIT: 'Quantificazione LCIA completata (ReCiPe 2016). Pathway: materiali, suolo, clima, ecotossicita.', status: 'PASS' },
  { phase: 'Assess', phaseIT: 'Analizzare', action: 'Risk assessment complete. 11 risks mapped (Physical, Transition, Systemic). Full documentation in docs/tnfd-risk-assessment.md.', actionIT: 'Risk assessment completato. 11 rischi mappati (Fisico, Transizione, Sistemico). Documentazione in docs/tnfd-risk-assessment.md.', status: 'PASS' },
  { phase: 'Prepare', phaseIT: 'Preparare', action: 'Targets set (-30% by 2027), ESRS E4 disclosure metrics mapped, NbS evaluated. See docs/tnfd-prepare-disclosure.md.', actionIT: 'Target definiti (-30% al 2027), metriche ESRS E4 mappate, NbS valutate. Vedi docs/tnfd-prepare-disclosure.md.', status: 'PASS' },
];

export const SITING_CRITERIA: SitingCriterion[] = [
  { criterion: 'Previous land use', criterionIT: 'Uso precedente del suolo', ourValue: 'Urban/Industrial', rating: 'Low', notes: 'Brownfield site — minimal additional biodiversity loss' },
  { criterion: 'Ecological connectivity', criterionIT: 'Connettivita ecologica', ourValue: 'Not in wildlife corridor', rating: 'Low', notes: 'Milan industrial district' },
  { criterion: 'Protected areas', criterionIT: 'Aree protette', ourValue: '>10 km from protected areas', rating: 'Low', notes: 'No proximity to Natura 2000 sites' },
  { criterion: 'Water stress', criterionIT: 'Stress idrico', ourValue: 'Medium (Po Valley)', rating: 'Medium', notes: 'Po Valley has seasonal water stress' },
  { criterion: 'Grid carbon intensity', criterionIT: 'Intensita carbonica rete', ourValue: '310 gCO2eq/kWh', rating: 'High', notes: 'Italy grid above EU average' },
];

// SIA A5 Biodiversity checklist
export const SIA_A5_BIODIVERSITY = [
  { item: 'LCIA biodiversity estimate (ReCiPe 2016)', itemIT: 'Stima biodiversita LCIA (ReCiPe 2016)', status: 'PASS' as Status },
  { item: 'Impact breakdown by pathway', itemIT: 'Ripartizione impatto per pathway', status: 'PASS' as Status },
  { item: 'Datacenter siting evaluation', itemIT: 'Valutazione sito datacenter', status: 'PASS' as Status },
  { item: 'Supply chain material assessment', itemIT: 'Valutazione materiali supply chain', status: 'PASS' as Status },
  { item: 'Nature-Based Solutions (NbS) evaluation', itemIT: 'Valutazione Nature-Based Solutions', status: 'PASS' as Status },
  { item: 'TNFD LEAP process complete (4/4)', itemIT: 'Processo TNFD LEAP completato (4/4)', status: 'PASS' as Status },
  { item: 'Biodiversity metrics in Impact Report', itemIT: 'Metriche biodiversita nel Report', status: 'PASS' as Status },
];

// ─── Priority Actions ───────────────────────────────────
export const PRIORITY_ACTIONS: PriorityAction[] = [
  { n: '1', action: 'React.lazy() on InboxPage, DataCapturePage, ReportingPage', priority: 'High', impact: 'DONE — routes lazy-loaded with Suspense fallback' },
  { n: '2', action: 'manualChunks for recharts, react-pdf, mui in Vite', priority: 'High', impact: 'DONE — each library in separate chunk' },
  { n: '3', action: 'Replace recharts sparkline in DMAOverview with CSS', priority: 'Medium', impact: 'DONE — pure MUI Box bars, no recharts dep' },
  { n: '4', action: 'Bundle visualizer + size budget in CI pipeline', priority: 'Medium', impact: 'DONE — vite-plugin-visualizer + GitLab CI budget check' },
  { n: '5', action: 'Backend stakeholder summary endpoint', priority: 'Medium', impact: 'DONE — GET /api/dma/stakeholder/summary' },
  { n: '6', action: 'WCAG AA ARIA labels on all components', priority: 'High', impact: 'DONE — charts, cards, tables, regions, flags' },
  { n: '7', action: 'Deploy backend in low-carbon region (Finland/France)', priority: 'Low', impact: 'PLANNED — -84% operational carbon (E x I)' },
];
